import { useEffect, useRef } from "react";
import styles from "./styles.module.scss";

export const useHighlight = (
  showRenderedPreview: boolean,
  activeDisplayIndex: number | null,
  setActiveDisplayIndex: React.Dispatch<React.SetStateAction<number | null>>,
  _previewVersion: "before" | "after",
  _approvedPreviewDocumentText: string,
  _originalPreviewDocumentText: string,
  _renderedPreviewHtml: string | null,
) => {
  const previewBodyRef = useRef<HTMLDivElement | null>(null);
  const previewHighlightedElementsRef = useRef<HTMLElement[]>([]);
  const previewScrollSourceRef = useRef<"card" | "preview" | null>(null);

  const handlePreviewActivate = (displayIndex: number) => {
    if (previewScrollSourceRef.current === "card") {
      return;
    }
    previewScrollSourceRef.current = "preview";
    setActiveDisplayIndex((prev) => (prev === displayIndex ? prev : displayIndex));
  };

  const handleCardActivate = (displayIndex: number) => {
    previewScrollSourceRef.current = "card";
    setActiveDisplayIndex((prev) => (prev === displayIndex ? prev : displayIndex));
  };

  useEffect(() => {
    const container = previewBodyRef.current;
    const removeHighlightClass = styles["renderedPreviewHighlightActive"];
    if (removeHighlightClass) {
      previewHighlightedElementsRef.current.forEach((el) =>
        el.classList.remove(removeHighlightClass),
      );
    }
    previewHighlightedElementsRef.current = [];

    if (!container || activeDisplayIndex === null) {
      previewScrollSourceRef.current = null;
      return;
    }

    const startMarker = container.querySelector(
      `[data-diff-hunk-start="${activeDisplayIndex}"]`,
    ) as HTMLElement | null;
    if (!startMarker) {
      previewScrollSourceRef.current = null;
      return;
    }

    const endMarker = container.querySelector(
      `[data-diff-hunk-end="${activeDisplayIndex}"]`,
    ) as HTMLElement | null;
    if (!endMarker) {
      previewScrollSourceRef.current = null;
      return;
    }

    const targets: HTMLElement[] = [];
    let current = startMarker.nextElementSibling as HTMLElement | null;

    while (current && current !== endMarker) {
      targets.push(current);
      current = current.nextElementSibling as HTMLElement | null;
    }

    if (targets.length === 0) {
      targets.push(startMarker);
    }

    const addHighlightClass = styles["renderedPreviewHighlightActive"];
    if (addHighlightClass) {
      targets.forEach((el) => el.classList.add(addHighlightClass));
    }
    previewHighlightedElementsRef.current = targets;

    if (previewScrollSourceRef.current === "card") {
      const scrollTarget = targets[0] ?? startMarker;
      scrollTarget?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    previewScrollSourceRef.current = null;
  }, [activeDisplayIndex]);

  useEffect(() => {
    if (!showRenderedPreview) {
      previewScrollSourceRef.current = null;
      return;
    }

    const container = previewBodyRef.current;
    if (!container) {
      previewScrollSourceRef.current = null;
      return;
    }

    const markers = Array.from(container.querySelectorAll<HTMLElement>("[data-diff-hunk-start]"));
    if (markers.length === 0) {
      previewScrollSourceRef.current = null;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (previewScrollSourceRef.current === "card") {
          return;
        }
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const first = visible[0];
        if (!first) {
          return;
        }
        const value = Number(first.target.getAttribute("data-diff-hunk-start"));
        if (Number.isNaN(value)) {
          return;
        }
        handlePreviewActivate(value);
      },
      {
        root: container,
        threshold: 0.1,
      },
    );

    markers.forEach((marker) => observer.observe(marker));

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlePreviewActivate is intentionally excluded to avoid infinite loop if not memoized in parent context
  }, [showRenderedPreview, handlePreviewActivate]);

  return {
    previewBodyRef,
    handleCardActivate,
  };
};
